import React, { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
  Alert,
  Pagination,
  Divider,
  Checkbox,
  RadioGroup,
  Radio,
  Autocomplete,
  Tooltip,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocalOffer as CouponIcon,
  Delete as DeleteIconSmall,
  Add as AddIconSmall,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Autorenew as AutorenewIcon,
  Campaign as CampaignIcon,
  LinkOff as LinkOffIcon,
  RestoreFromTrash as RestoreIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import InputAdornment from '@mui/material/InputAdornment';
import {
  buildCouponCodeBase,
  collectCouponCodesFromList,
  ensureUniqueCouponCode,
  generateCouponCode,
  generateDefaultCouponCode,
  normalizeCouponCode,
} from '../../utils/couponCodeGenerator';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { format } from 'date-fns';
import { servicesApi } from '../../services/smartBillApi';
import ProfessionRestrictionSelect from '../../components/ProfessionRestrictionSelect';
import {
  ALL_PROFESSION,
  fromAllowedProfessionsPayload,
  toAllowedProfessionsPayload,
} from '../../utils/professions';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/** ASCII-safe currency (avoids corrupted rupee symbol in some Windows encodings). */
const formatRs = (amount) => {
  const value = parseFloat(amount);
  if (Number.isNaN(value)) return 'Rs.0';
  return `Rs.${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)}`;
};

/**
 * Template coupons with validity_start_type=issued_date keep a placeholder valid_until;
 * real expiry is calculated when the coupon is issued to a customer.
 * Do not hide those templates from the Active tab based on valid_until.
 */
const isTemplateCouponExpired = (coupon) => {
  if (!coupon) return false;
  const status = coupon.status || 'active';
  if (status === 'expired') return true;
  const validityStartType = coupon.validity_start_type || 'issued_date';
  // issued_date templates stay listable until paused/deleted; expiry applies per issued copy
  if (validityStartType === 'issued_date') {
    return false;
  }
  const validUntil = coupon.valid_until;
  return validUntil ? new Date(validUntil) < new Date() : false;
};

const isTemplateCouponUsed = (coupon) => {
  if (!coupon) return false;
  const status = coupon.status || 'active';
  const maxUses = coupon.max_uses;
  const currentUses = coupon.current_uses || 0;
  return status === 'used' || (maxUses && currentUses >= maxUses);
};

const isTemplateCouponActive = (coupon) => {
  if (!coupon) return false;
  const status = coupon.status || 'active';
  return status === 'active' && !isTemplateCouponExpired(coupon) && !isTemplateCouponUsed(coupon);
};

const offerTypes = [
  { value: 'fixed', label: 'Fixed Offer (Same price throughout validity)' },
  { value: 'increasing', label: 'Day-wise Increasing Offer (Price increases each day)' },
];

const couponTypes = [
  { value: 'internal', label: 'Internal' },
  { value: 'partner', label: 'Partner' },
  { value: 'seasonal', label: 'Seasonal' },
];

// Validation is handled in the onSubmit function
const validationSchema = yup.object({
  coupon_name: yup.string().required('Coupon name is required'),
  coupon_code: yup.string().required('Coupon code is required'),
});

const CouponsManagement = ({ embedded = false } = {}) => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState(''); // Input value (for display)
  const [search, setSearch] = useState(''); // Debounced search value (for query)
  const [statusFilter, setStatusFilter] = useState('active');
  const [typeFilter, setTypeFilter] = useState('all');
  const [shareFilter, setShareFilter] = useState('all'); // all | shared | private
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [dayWisePricing, setDayWisePricing] = useState({});
  const [selectedProfessions, setSelectedProfessions] = useState([ALL_PROFESSION]);
  const [otherProfessionText, setOtherProfessionText] = useState('');
  const [showNewServiceDialog, setShowNewServiceDialog] = useState(false);
  const [newServiceData, setNewServiceData] = useState({
    name: '',
    main_category: '',
    sub_category: '',
    price: '',
    challana: '',
  });
  const [savingNewService, setSavingNewService] = useState(false);
  const [serviceInputValue, setServiceInputValue] = useState('');
  const [showServiceSuggestions, setShowServiceSuggestions] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState(new Set());
  const [couponCodeManual, setCouponCodeManual] = useState(false);
  const [generatingCouponCode, setGeneratingCouponCode] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Load services function - Include ALL services (active and inactive) for coupon creation
  const loadServices = async () => {
    setServicesLoading(true);
    try {
      // Pass includeInactive = true to get ALL services from Manage Services & Bills
      const response = await servicesApi.getAll('', '', true);
      if (response.success && response.data && Array.isArray(response.data)) {
        // Sort services by name for better UX
        const sortedServices = [...response.data].sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setServices(sortedServices);
        console.log(`Loaded ${sortedServices.length} services for coupon management:`, sortedServices.map(s => s.name));
      } else {
        console.warn('No services data in response:', response);
        setServices([]);
      }
    } catch (err) {
      console.error('Error loading services:', err);
      toast.error('Failed to load services. Please try again.');
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  };

  // Load services on component mount and when dialog opens
  useEffect(() => {
    loadServices();
  }, []);

  // Reload services when dialog opens to get latest services
  useEffect(() => {
    if (dialogOpen) {
      loadServices();
    }
  }, [dialogOpen]);

  // Debounce search input to prevent focus loss
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      // Reset to page 1 when search changes
      setPage(1);
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Helper function to get authentication token
  const getAuthToken = () => {
    const sessionType = window.location.pathname.startsWith('/user/') ? 'user' : 'admin';
    const storageKeys = sessionType === 'user' 
      ? { token: 'user_token', user: 'user_user' }
      : { token: 'admin_token', user: 'admin_user' };
    const token = localStorage.getItem(storageKeys.token) || localStorage.getItem('token');
    
    if (!token) {
      throw new Error('Authentication required. Please log in.');
    }
    
    return token;
  };

  // Fetch all coupons for client-side filtering (needed for accurate tab counts)
  const { data: allCouponsData } = useQuery(
    ['coupons-all', search, typeFilter],
    async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API_BASE_URL}/coupons`, {
          params: {
            page: 1,
            limit: 10000, // Get all for accurate counts
            search,
            status: '', // Get all statuses
            coupon_type: typeFilter !== 'all' ? typeFilter : '',
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        return response.data;
      } catch (err) {
        console.error('❌ Error fetching all coupons:', err);
        return { coupons: [] };
      }
    },
    {
      enabled: true,
      retry: false,
    }
  );

  const { data: trashCountData } = useQuery(
    ['coupons-trash-count'],
    async () => {
      try {
        const token = getAuthToken();
        const response = await axios.get(`${API_BASE_URL}/coupons`, {
          params: { page: 1, limit: 1, trash: true },
          headers: { Authorization: `Bearer ${token}` },
        });
        return response.data?.pagination?.total || 0;
      } catch {
        return 0;
      }
    },
    { retry: false }
  );

  const { data, isLoading, error } = useQuery(
    ['coupons', page, search, statusFilter, typeFilter, shareFilter],
    async () => {
      try {
        const token = getAuthToken();

        if (statusFilter === 'allotted') {
          const response = await axios.get(`${API_BASE_URL}/coupons/issued`, {
            params: { limit: 200, search: search || undefined },
            headers: { Authorization: `Bearer ${token}` },
          });
          const list = response.data?.coupons || [];
          return {
            coupons: list,
            pagination: { total: list.length, totalPages: 1, page: 1, limit: list.length || 10 },
          };
        }
        
        console.log('📡 Fetching coupons from:', `${API_BASE_URL}/coupons`);
        const isTrash = statusFilter === 'trash';
        // For 'used' status, fetch all and filter client-side (templates don't have 'used' status in backend)
        const backendStatus = statusFilter === 'used' || isTrash ? '' : (statusFilter !== 'all' ? statusFilter : '');
        // For 'used', fetch all coupons; for others, use pagination
        const fetchLimit = statusFilter === 'used' ? 10000 : (isTrash ? 100 : 10);
      const response = await axios.get(`${API_BASE_URL}/coupons`, {
        params: {
            page: statusFilter === 'used' ? 1 : page, // For 'used', always fetch from page 1
            limit: fetchLimit,
          search,
            status: backendStatus,
          coupon_type: typeFilter !== 'all' ? typeFilter : '',
          ...(isTrash ? { trash: true } : {}),
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
        
        console.log('✅ Coupons fetched successfully:', response.data);
        
        // Validate response structure
        if (!response.data) {
          throw new Error('Invalid response: no data received');
        }
        
        if (!Array.isArray(response.data.coupons)) {
          console.warn('⚠️ Response coupons is not an array:', response.data);
          return {
            ...response.data,
            coupons: Array.isArray(response.data) ? response.data : []
          };
        }
        
        // Filter client-side for 'used' status (templates don't have 'used' status in backend)
        // For 'used', we fetch all coupons and filter client-side, then paginate
        let filteredCoupons = response.data.coupons;
        if (statusFilter === 'used') {
          filteredCoupons = response.data.coupons.filter(isTemplateCouponUsed);
          // Apply pagination to filtered results
          const startIndex = (page - 1) * 10;
          const endIndex = startIndex + 10;
          filteredCoupons = filteredCoupons.slice(startIndex, endIndex);
        } else if (statusFilter === 'active') {
          // Active templates: status active, not used; issued_date templates ignore placeholder valid_until
          filteredCoupons = response.data.coupons.filter(isTemplateCouponActive);
        } else if (statusFilter === 'expired') {
          filteredCoupons = response.data.coupons.filter(isTemplateCouponExpired);
        }

        if (shareFilter === 'shared') {
          filteredCoupons = filteredCoupons.filter(
            (c) => c.share_with_locsy === true || c.shareWithLocsy === true
          );
        } else if (shareFilter === 'private') {
          filteredCoupons = filteredCoupons.filter(
            (c) => !(c.share_with_locsy === true || c.shareWithLocsy === true)
          );
        }
        
        // For 'used', we need to recalculate pagination from all filtered coupons
        // For others, backend pagination is mostly correct, but we adjust for client-side filtering
        const allFilteredCount = statusFilter === 'used' 
          ? (allCouponsData?.coupons?.filter(isTemplateCouponUsed).length || 0)
          : filteredCoupons.length;
        
        return {
          ...response.data,
          coupons: filteredCoupons,
          pagination: {
            ...response.data.pagination,
            total: statusFilter === 'used' ? allFilteredCount : response.data.pagination?.total || filteredCoupons.length,
            totalPages: Math.ceil((statusFilter === 'used' ? allFilteredCount : response.data.pagination?.total || filteredCoupons.length) / 10),
          }
        };
      } catch (err) {
        console.error('❌ Error fetching coupons:', err);
        console.error('   Response:', err.response?.data);
        console.error('   Status:', err.response?.status);
        console.error('   Message:', err.message);
        console.error('   Stack:', err.stack);
        throw err;
      }
    },
    {
      retry: (failureCount, error) => {
        // Don't retry on 401 errors (unauthorized)
        if (error?.response?.status === 401) {
          console.error('❌ Unauthorized (401) - Authentication failed');
          return false;
        }
        return failureCount < 2;
      },
      onError: (error) => {
        console.error('❌ React Query error:', error);
      },
    }
  );

  const createMutation = useMutation(
    async (values) => {
      const token = getAuthToken();
      const response = await axios.post(`${API_BASE_URL}/coupons`, values, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Coupon created successfully');
        queryClient.invalidateQueries('coupons');
        formik.resetForm();
        setDayWisePricing({});
        setSelectedProfessions([ALL_PROFESSION]);
    setOtherProfessionText('');
        setServiceInputValue('');
        setDialogOpen(false);
        setEditingCoupon(null);
      },
      onError: (error) => {
        console.error('❌ Create coupon error:', error);
        if (error.response?.status === 401) {
          toast.error('Session expired. Please log in again.');
          setTimeout(() => {
            window.location.href = '/aalogin';
          }, 1500);
        } else {
        toast.error(error.response?.data?.message || 'Failed to create coupon');
        }
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, ...values }) => {
      const token = getAuthToken();
      const response = await axios.put(`${API_BASE_URL}/coupons/${id}`, values, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Coupon updated successfully');
        queryClient.invalidateQueries('coupons');
        formik.resetForm();
        setDayWisePricing({});
        setSelectedProfessions([ALL_PROFESSION]);
    setOtherProfessionText('');
        setServiceInputValue('');
        setDialogOpen(false);
        setEditingCoupon(null);
      },
      onError: (error) => {
        console.error('❌ Update coupon error:', error);
        if (error.response?.status === 401) {
          toast.error('Session expired. Please log in again.');
          setTimeout(() => {
            window.location.href = '/aalogin';
          }, 1500);
        } else {
        toast.error(error.response?.data?.message || 'Failed to update coupon');
        }
      },
    }
  );

  const deleteMutation = useMutation(
    async ({ id, permanent = false }) => {
      const token = getAuthToken();
      const response = await axios.delete(`${API_BASE_URL}/coupons/${id}`, {
        params: permanent ? { permanent: true } : undefined,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data?.message || (data?.permanent ? 'Permanently deleted' : 'Moved to recycle bin'));
        queryClient.invalidateQueries('coupons');
        queryClient.invalidateQueries('coupons-all');
        queryClient.invalidateQueries('coupons-trash-count');
        setDeleteDialog(null);
        setSelectedCoupons(new Set());
        setSelectAll(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete coupon');
      },
    }
  );

  const restoreMutation = useMutation(
    async (id) => {
      const token = getAuthToken();
      const response = await axios.post(
        `${API_BASE_URL}/coupons/${id}/restore`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Coupon restored');
        queryClient.invalidateQueries('coupons');
        queryClient.invalidateQueries('coupons-all');
        queryClient.invalidateQueries('coupons-trash-count');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to restore coupon');
      },
    }
  );

  const bulkDeleteMutation = useMutation(
    async (ids) => {
      const token = getAuthToken();
      const response = await axios.delete(`${API_BASE_URL}/coupons/bulk`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { ids },
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(`Successfully deleted ${data.deletedCount} coupon(s)`);
        queryClient.invalidateQueries('coupons');
        setDeleteDialog(null);
        setSelectedCoupons(new Set());
        setSelectAll(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete coupons');
      },
    }
  );

  const toggleStatusMutation = useMutation(
    async ({ id, action }) => {
      const token = getAuthToken();
      const response = await axios.patch(
        `${API_BASE_URL}/coupons/${id}/status`,
        { action },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Coupon status updated successfully');
        queryClient.invalidateQueries('coupons');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update coupon status');
      },
    }
  );

  const handleToggleStatus = (coupon, action) => {
    const couponId = coupon.id || coupon.coupon_id;
    toggleStatusMutation.mutate({ id: couponId, action });
  };

  const toggleShareMutation = useMutation(
    async ({ id, shareWithLocsy }) => {
      const token = getAuthToken();
      const response = await axios.put(
        `${API_BASE_URL}/coupons/${id}`,
        { share_with_locsy: shareWithLocsy },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    {
      onSuccess: (_, vars) => {
        toast.success(
          vars.shareWithLocsy
            ? 'Shared with Locsy DigiDrop'
            : 'Removed from Locsy DigiDrop pool'
        );
        queryClient.invalidateQueries('coupons');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update Share with Locsy');
      },
    }
  );

  const handleToggleShare = (coupon, shareWithLocsy) => {
    const couponId = coupon.id || coupon.coupon_id;
    toggleShareMutation.mutate({ id: couponId, shareWithLocsy });
  };

  const editingCouponId = editingCoupon?.id || editingCoupon?.coupon_id || null;

  const formik = useFormik({
    initialValues: {
      coupon_name: '',
      coupon_code: '',
      coupon_type: 'internal',
      service_id: '',
      service_name: '',
      actual_price: '',
      offer_type: 'fixed',
      offer_price: '',
      validity_days: '',
      validity_start_type: 'issued_date', // 'issued_date' or 'generated_date'
      day_wise_pricing: {},
      description: '',
      is_service_based: true,
      allowed_professions: [],
      share_with_locsy: true, // DigiDrop default ON — Mitra can turn off
      coupon_worth: '',
      digidrop_pool_qty: '100',
      // Place coupons (Mitra-style): % / flat / Buy X Get Y
      discount_type: 'flat',
      discount_value: '',
      buy_product_id: '',
      buy_product_name: '',
      buy_unit_price: '',
      get_product_id: '',
      get_product_name: '',
      get_unit_price: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Validate service-based fields
      if (values.is_service_based) {
        if (!values.service_id) {
          toast.error('Please select a service');
          return;
        }
        if (!values.actual_price || values.actual_price <= 0) {
          toast.error('Please enter a valid actual price');
          return;
        }
        if (!values.offer_type) {
          toast.error('Please select an offer type');
          return;
        }
        if (!values.validity_days || values.validity_days < 1) {
          toast.error('Please enter validity days (1-365)');
          return;
        }
        if (values.offer_type === 'fixed' && (!values.offer_price || values.offer_price <= 0)) {
          toast.error('Please enter a valid offer price for fixed offer');
          return;
        }
        if (values.offer_type === 'increasing') {
          const pricingKeys = Object.keys(dayWisePricing).filter(
            (key) => dayWisePricing[key] && dayWisePricing[key] > 0
          );
          if (pricingKeys.length === 0) {
            toast.error('Please add at least one day-wise price');
            return;
          }
        }
      } else if (values.discount_type === 'buy_x_get_y') {
        if (!String(values.buy_product_name || '').trim() || !String(values.get_product_name || '').trim()) {
          toast.error('Buy X Get Y needs both Buy product (X) and Get free product (Y)');
          return;
        }
        const getPrice = parseFloat(values.get_unit_price);
        if (Number.isNaN(getPrice) || getPrice < 0) {
          toast.error('Enter Get product (Y) list price');
          return;
        }
      } else if (!values.is_service_based) {
        const dv = parseFloat(values.discount_value);
        if (Number.isNaN(dv) || dv <= 0) {
          toast.error('Enter a valid discount value');
          return;
        }
      }

      const submitValues = {
        coupon_name: values.coupon_name,
        coupon_code: values.coupon_code,
        coupon_type: values.coupon_type,
        description: values.description || null,
        allowed_professions: toAllowedProfessionsPayload(selectedProfessions, otherProfessionText),
        share_with_locsy: values.share_with_locsy !== false,
      };

      if (values.share_with_locsy !== false) {
        const worthNum = parseFloat(values.coupon_worth);
        if (!Number.isNaN(worthNum) && worthNum >= 0) {
          submitValues.coupon_worth = worthNum;
        } else if (
          values.is_service_based &&
          values.actual_price &&
          values.offer_price
        ) {
          const a = parseFloat(values.actual_price);
          const o = parseFloat(values.offer_price);
          if (!Number.isNaN(a) && !Number.isNaN(o) && a > o) {
            submitValues.coupon_worth = a - o;
          }
        }
        const poolNum = parseInt(values.digidrop_pool_qty, 10);
        if (!Number.isNaN(poolNum) && poolNum >= 0) {
          submitValues.digidrop_pool_qty = poolNum;
        }
      }

      // If service-based, add service fields
      if (values.is_service_based) {
        const selectedService = services.find((s) => s.id === values.service_id);
        submitValues.service_id = values.service_id;
        submitValues.service_name = selectedService?.name || values.service_name;
        submitValues.actual_price = parseFloat(values.actual_price);
        submitValues.offer_type = values.offer_type;
        submitValues.validity_days = parseInt(values.validity_days);
        submitValues.validity_start_type = values.validity_start_type || 'issued_date';
        // Start date is automatically set to today (coupon generation date) on backend

        if (values.offer_type === 'fixed') {
          submitValues.offer_price = parseFloat(values.offer_price);
        } else if (values.offer_type === 'increasing') {
          // Clean up empty values from day_wise_pricing
          const cleanedPricing = {};
          Object.keys(dayWisePricing).forEach((day) => {
            if (dayWisePricing[day] && dayWisePricing[day] > 0) {
              cleanedPricing[day] = dayWisePricing[day];
            }
          });
          submitValues.day_wise_pricing = cleanedPricing;
        }
      } else {
        // Place coupon: % / flat / Buy X Get Y (same pipeline as Mitra Flutter)
        submitValues.discount_type = values.discount_type || 'flat';
        if (values.discount_type === 'buy_x_get_y') {
          const buyPrice = parseFloat(values.buy_unit_price) || 0;
          const getPrice = parseFloat(values.get_unit_price) || 0;
          submitValues.bxgy_config = {
            buy_product_id: values.buy_product_id || null,
            buy_product_name: String(values.buy_product_name || '').trim(),
            buy_qty: 1,
            buy_unit_price: buyPrice,
            get_product_id: values.get_product_id || null,
            get_product_name: String(values.get_product_name || '').trim(),
            get_qty: 1,
            get_unit_price: getPrice,
          };
          submitValues.discount_value = getPrice;
          if (submitValues.coupon_worth == null) {
            submitValues.coupon_worth = getPrice;
          }
          submitValues.actual_price = buyPrice + getPrice;
          submitValues.offer_price = buyPrice;
        } else {
          const dv = parseFloat(values.discount_value);
          submitValues.discount_value = dv;
          if (submitValues.coupon_worth == null) {
            submitValues.coupon_worth = dv;
          }
        }
      }

      if (editingCoupon) {
        updateMutation.mutate({ id: editingCoupon.id || editingCoupon.coupon_id, ...submitValues });
      } else {
        createMutation.mutate(submitValues);
      }
    },
    enableReinitialize: true,
  });

  const getTakenCouponCodes = useCallback(() => {
    const list = allCouponsData?.coupons || data?.coupons || [];
    return collectCouponCodesFromList(list, editingCouponId);
  }, [allCouponsData?.coupons, data?.coupons, editingCouponId]);

  const applyAutoCouponCode = useCallback(
    async ({ force = false } = {}) => {
      const params = {
        couponName: formik.values.coupon_name,
        offerType: formik.values.offer_type,
        offerPrice: formik.values.offer_price,
        actualPrice: formik.values.actual_price,
        dayWisePricing,
      };

      if (!force && couponCodeManual && !editingCoupon) return;

      setGeneratingCouponCode(true);
      try {
        const taken = getTakenCouponCodes();
        const fromNamePrice = generateCouponCode(params, taken, editingCouponId);
        let code = fromNamePrice || ensureUniqueCouponCode(
          generateDefaultCouponCode(),
          taken,
          editingCouponId,
        );
        if (!code) code = generateDefaultCouponCode();

        const token = getAuthToken();
        for (let attempt = 0; attempt < 20; attempt += 1) {
          try {
            const response = await axios.get(`${API_BASE_URL}/coupons/check-code`, {
              params: {
                code,
                ...(editingCouponId ? { excludeId: editingCouponId } : {}),
              },
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data?.available) {
              formik.setFieldValue('coupon_code', code);
              if (!editingCoupon) setCouponCodeManual(false);
              return;
            }
          } catch (checkErr) {
            console.warn('Coupon code check API failed, using local list only', checkErr);
            break;
          }

          taken.push(code);
          const nextBase = buildCouponCodeBase(params) || normalizeCouponCode(code) || generateDefaultCouponCode();
          code = ensureUniqueCouponCode(nextBase, taken, editingCouponId);
        }

        formik.setFieldValue('coupon_code', code);
      } finally {
        setGeneratingCouponCode(false);
      }
    },
    [
      dayWisePricing,
      editingCoupon,
      editingCouponId,
      couponCodeManual,
      getTakenCouponCodes,
      formik.values.coupon_name,
      formik.values.offer_type,
      formik.values.offer_price,
      formik.values.actual_price,
      formik.setFieldValue,
    ],
  );

  useEffect(() => {
    if (!dialogOpen || couponCodeManual || editingCoupon) return;

    const timer = setTimeout(() => {
      applyAutoCouponCode({ force: false });
    }, 350);

    return () => clearTimeout(timer);
  }, [
    dialogOpen,
    editingCoupon,
    couponCodeManual,
    formik.values.coupon_name,
    formik.values.offer_price,
    formik.values.actual_price,
    formik.values.offer_type,
    dayWisePricing,
    applyAutoCouponCode,
  ]);

  const handleEdit = (coupon) => {
    setCouponCodeManual(true);
    setEditingCoupon(coupon);
    
    // Parse day_wise_pricing if it's a string
    let parsedDayWisePricing = {};
    if (coupon.day_wise_pricing) {
      if (typeof coupon.day_wise_pricing === 'string') {
        try {
          parsedDayWisePricing = JSON.parse(coupon.day_wise_pricing);
        } catch (e) {
          parsedDayWisePricing = {};
        }
      } else {
        parsedDayWisePricing = coupon.day_wise_pricing;
      }
    }
    setDayWisePricing(parsedDayWisePricing);

    // Parse allowed_professions (empty/null => ALL default)
    const parsedProfessions = fromAllowedProfessionsPayload(coupon.allowed_professions);
    setSelectedProfessions(parsedProfessions);
    setOtherProfessionText('');

    formik.setValues({
      coupon_name: coupon.coupon_name || coupon.name || '',
      coupon_code: coupon.coupon_code || coupon.code || '',
      coupon_type: coupon.coupon_type || 'internal',
      service_id: coupon.service_id || '',
      service_name: coupon.service_name || '',
      actual_price: coupon.actual_price || '',
      offer_type: coupon.offer_type || 'fixed',
      offer_price: coupon.offer_price || '',
      validity_days: coupon.validity_days || '',
      validity_start_type: coupon.validity_start_type || 'issued_date',
      day_wise_pricing: parsedDayWisePricing,
      description: coupon.description || '',
      is_service_based:
        !!(coupon.service_id || coupon.service_name) &&
        coupon.discount_type !== 'buy_x_get_y' &&
        !coupon.bxgy_config,
      allowed_professions: parsedProfessions,
      share_with_locsy: coupon.share_with_locsy === true || coupon.shareWithLocsy === true,
      coupon_worth:
        coupon.coupon_worth != null && coupon.coupon_worth !== ''
          ? String(coupon.coupon_worth)
          : '',
      digidrop_pool_qty:
        coupon.digidrop_pool_qty != null && coupon.digidrop_pool_qty !== ''
          ? String(coupon.digidrop_pool_qty)
          : '100',
      discount_type: coupon.discount_type || 'flat',
      discount_value:
        coupon.discount_value != null
          ? String(coupon.discount_value)
          : coupon.discount_amount != null
            ? String(coupon.discount_amount)
            : '',
      buy_product_id: coupon.bxgy_config?.buy_product_id || '',
      buy_product_name: coupon.bxgy_config?.buy_product_name || '',
      buy_unit_price:
        coupon.bxgy_config?.buy_unit_price != null
          ? String(coupon.bxgy_config.buy_unit_price)
          : '',
      get_product_id: coupon.bxgy_config?.get_product_id || '',
      get_product_name: coupon.bxgy_config?.get_product_name || '',
      get_unit_price:
        coupon.bxgy_config?.get_unit_price != null
          ? String(coupon.bxgy_config.get_unit_price)
          : '',
    });
    setServiceInputValue(coupon.service_name || '');
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingCoupon(null);
    setCouponCodeManual(false);
    const starter = generateDefaultCouponCode();
    formik.resetForm({
      values: {
        ...formik.initialValues,
        coupon_code: starter,
      },
    });
    setDayWisePricing({});
    setSelectedProfessions([ALL_PROFESSION]);
    setOtherProfessionText('');
    setServiceInputValue('');
    setDialogOpen(true);
  };

  // Reset selections when filters change
  useEffect(() => {
    setSelectedCoupons(new Set());
    setSelectAll(false);
  }, [search, statusFilter, typeFilter, shareFilter, page]);

  const handleSelectCoupon = (couponId) => {
    const idString = String(couponId);
    const newSelected = new Set(selectedCoupons);
    if (newSelected.has(idString)) {
      newSelected.delete(idString);
    } else {
      newSelected.add(idString);
    }
    setSelectedCoupons(newSelected);
    setSelectAll(newSelected.size === data?.coupons?.length && data?.coupons?.length > 0);
  };

  const fetchAllMatchingCouponIds = async () => {
    try {
      const token = getAuthToken();
      const params = {
        limit: 10000,
        offset: 0,
        search,
        status: statusFilter !== 'all' ? statusFilter : '',
        coupon_type: typeFilter !== 'all' ? typeFilter : '',
      };
      
      const response = await axios.get(`${API_BASE_URL}/coupons`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const couponsData = response.data?.coupons || [];
      const ids = couponsData.map(coupon => String(coupon.id || coupon.coupon_id)).filter(id => id && id !== 'undefined');
      return ids;
    } catch (err) {
      console.error('Error fetching all matching coupon IDs:', err);
      toast.error('Failed to fetch all matching coupons');
      return [];
    }
  };

  const handleSelectAll = async () => {
    if (selectAll) {
      setSelectedCoupons(new Set());
      setSelectAll(false);
    } else {
      const allMatchingIds = await fetchAllMatchingCouponIds();
      if (allMatchingIds.length > 0) {
        const newSet = new Set(allMatchingIds);
        setSelectedCoupons(newSet);
        setSelectAll(true);
        toast.info(`Selected ${allMatchingIds.length} coupon(s) matching your search/filter`);
      } else {
        toast.warning('No coupons found to select');
      }
    }
  };

  const handleDeleteClick = (coupon) => {
    if (coupon) {
      // Single delete
      setDeleteDialog(coupon);
    } else if (selectedCoupons.size > 0) {
      // Bulk delete
      setDeleteDialog({ bulk: true, count: selectedCoupons.size });
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog?.bulk) {
      // Bulk delete
      setBulkDeleting(true);
      try {
        const idsToDelete = Array.from(selectedCoupons);
        await bulkDeleteMutation.mutateAsync(idsToDelete);
      } catch (error) {
        console.error('Bulk delete error:', error);
      } finally {
        setBulkDeleting(false);
      }
    } else if (deleteDialog?.id || deleteDialog?.coupon_id) {
      // Single delete → recycle bin (or permanent if already in trash)
      const id = deleteDialog.id || deleteDialog.coupon_id;
      deleteMutation.mutate({
        id,
        permanent: statusFilter === 'trash' || deleteDialog?.permanent === true,
      });
    }
  };

  /** List price − offer price → DigiDrop coupon worth (keeps UX simple). */
  const syncCouponWorthFromPrices = (actual, offer) => {
    const a = parseFloat(actual);
    const o = parseFloat(offer);
    if (!Number.isNaN(a) && !Number.isNaN(o) && a > o) {
      const worth = Math.round((a - o) * 100) / 100;
      formik.setFieldValue('coupon_worth', String(worth));
    }
  };

  const handleServiceInputChange = (event, newValue, reason) => {
    if (reason === 'selectOption' && newValue) {
      // User selected a service from suggestions
      const selectedService = typeof newValue === 'string' 
        ? services.find(s => s.name === newValue)
        : newValue;
      
      if (selectedService && selectedService.id) {
        formik.setFieldValue('service_id', selectedService.id);
        formik.setFieldValue('service_name', selectedService.name);
        // Set service name as default coupon name if coupon name is empty
        if (!formik.values.coupon_name || formik.values.coupon_name.trim() === '') {
          formik.setFieldValue('coupon_name', selectedService.name);
        }
        if (selectedService.price) {
          formik.setFieldValue('actual_price', selectedService.price);
          syncCouponWorthFromPrices(
            selectedService.price,
            formik.values.offer_price
          );
        }
        setServiceInputValue(selectedService.name);
      }
    } else if (reason === 'input') {
      // User is typing
      setServiceInputValue(newValue || '');
    }
  };

  const handleServiceInputBlur = () => {
    // Check if typed value matches any existing service
    const typedValue = serviceInputValue.trim();
    if (!typedValue) {
      setServiceInputValue('');
      return;
    }

    const matchedService = services.find(
      s => s.name.toLowerCase() === typedValue.toLowerCase()
    );

    if (matchedService) {
      // Match found - use it
      formik.setFieldValue('service_id', matchedService.id);
      formik.setFieldValue('service_name', matchedService.name);
      // Set service name as default coupon name if coupon name is empty
      if (!formik.values.coupon_name || formik.values.coupon_name.trim() === '') {
        formik.setFieldValue('coupon_name', matchedService.name);
      }
      if (matchedService.price) {
        formik.setFieldValue('actual_price', matchedService.price);
        syncCouponWorthFromPrices(
          matchedService.price,
          formik.values.offer_price
        );
      }
      setServiceInputValue(matchedService.name);
    } else {
      // No match - show dialog to add new service
      setNewServiceData({
        name: typedValue,
        main_category: '',
        sub_category: '',
        price: '',
        challana: '',
      });
      setShowNewServiceDialog(true);
    }
  };

  const handleServiceChange = (serviceId) => {
    if (serviceId === '__add_new_service__') {
      // Open dialog to add new service
      setNewServiceData({
        name: serviceInputValue || '',
        main_category: '',
        sub_category: '',
        price: '',
        challana: '',
      });
      setShowNewServiceDialog(true);
      return;
    }
    
    const selectedService = services.find((s) => s.id === serviceId);
    formik.setFieldValue('service_id', serviceId);
    formik.setFieldValue('service_name', selectedService?.name || '');
    if (selectedService?.price) {
      formik.setFieldValue('actual_price', selectedService.price);
      syncCouponWorthFromPrices(
        selectedService.price,
        formik.values.offer_price
      );
    }
    setServiceInputValue(selectedService?.name || '');
  };

  const handleSaveNewService = async () => {
    if (!newServiceData.name?.trim() || savingNewService) {
      toast.error('Please enter a service name');
      return;
    }

    if (!newServiceData.price || parseFloat(newServiceData.price) <= 0) {
      toast.error('Please enter a valid price');
      return;
    }

    setSavingNewService(true);
    try {
      const payload = {
        name: newServiceData.name.trim(),
        main_category: newServiceData.main_category.trim() || 'Custom',
        sub_category: newServiceData.sub_category.trim() || '',
        price: parseFloat(newServiceData.price) || 0,
        challana: parseFloat(newServiceData.challana) || 0,
        keywords: [newServiceData.name.trim().toLowerCase()],
        has_dynamic_sub_service: false,
        has_dynamic_price: false,
        is_active: true,
      };

      const response = await servicesApi.create(payload);
      if (response?.success && response.data) {
        const saved = response.data;
        
        // Add to services list
        const newService = {
          id: saved.id,
          name: saved.name,
          price: saved.price,
          main_category: saved.main_category,
          sub_category: saved.sub_category,
          is_active: saved.is_active,
        };
        
        setServices((prev) => [...prev, newService]);
        
        // Select the newly created service
        formik.setFieldValue('service_id', saved.id);
        formik.setFieldValue('service_name', saved.name);
        // Set service name as default coupon name if coupon name is empty
        if (!formik.values.coupon_name || formik.values.coupon_name.trim() === '') {
          formik.setFieldValue('coupon_name', saved.name);
        }
        formik.setFieldValue('actual_price', saved.price);
        syncCouponWorthFromPrices(saved.price, formik.values.offer_price);
        setServiceInputValue(saved.name);
        
        toast.success(`Service "${saved.name}" created successfully`);
        setShowNewServiceDialog(false);
        setNewServiceData({
          name: '',
          main_category: '',
          sub_category: '',
          price: '',
          challana: '',
        });
      } else {
        throw new Error(response?.message || 'Failed to create service');
      }
    } catch (error) {
      console.error('Error creating service:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to create service');
    } finally {
      setSavingNewService(false);
    }
  };

  const handleCancelNewService = () => {
    setShowNewServiceDialog(false);
    setNewServiceData({
      name: '',
      main_category: '',
      sub_category: '',
      price: '',
      challana: '',
    });
  };

  const handleAddDayWisePrice = () => {
    const days = Object.keys(dayWisePricing).map(Number);
    const maxDay = days.length > 0 ? Math.max(...days) : 0;
    const newDay = maxDay + 1;
    // If adding day 1 and offer_price exists, use it as default
    const defaultPrice = newDay === 1 && formik.values.offer_price ? formik.values.offer_price : '';
    setDayWisePricing({ ...dayWisePricing, [newDay]: defaultPrice });
  };

  const handleRemoveDayWisePrice = (day) => {
    const newPricing = { ...dayWisePricing };
    delete newPricing[day];
    setDayWisePricing(newPricing);
  };

  const handleDayWisePriceChange = (day, price) => {
    setDayWisePricing({ ...dayWisePricing, [day]: price ? parseFloat(price) : '' });
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      inactive: 'default',
      expired: 'error',
    };
    return colors[status] || 'default';
  };

  const getTypeColor = (type) => {
    const colors = {
      internal: 'primary',
      partner: 'secondary',
      seasonal: 'warning',
    };
    return colors[type] || 'default';
  };

  const getOfferDisplay = (coupon) => {
    if (coupon.service_id || coupon.service_name) {
      if (coupon.offer_type === 'fixed') {
        return `Fixed: ${formatRs(coupon.actual_price)} -> ${formatRs(coupon.offer_price)} (${coupon.validity_days} days)`;
      } else if (coupon.offer_type === 'increasing') {
        const dayPricing = typeof coupon.day_wise_pricing === 'string'
          ? JSON.parse(coupon.day_wise_pricing)
          : coupon.day_wise_pricing;
        const days = Object.keys(dayPricing || {}).sort((a, b) => Number(a) - Number(b));
        if (days.length > 0) {
          const firstDay = days[0];
          const lastDay = days[days.length - 1];
          return `Increasing: Day ${firstDay} ${formatRs(dayPricing[firstDay])} -> Day ${lastDay} ${formatRs(dayPricing[lastDay])}`;
        }
        return 'Day-wise increasing offer';
      }
    }
    return 'Standard coupon';
  };

  // Helper function to calculate validity display for form
  const calculateValidityDisplay = (validityDays, validityStartType, editingCouponData) => {
    try {
      const days = parseInt(validityDays);
      const startType = validityStartType || 'issued_date';
      
      if (!validityDays || !days || days < 1 || isNaN(days)) {
        return 'Enter validity days';
      }
      
      if (startType === 'generated_date') {
        // Calculate expired date from generation date + validity_days
        let startDate;
        if (editingCouponData) {
          if (editingCouponData.generated_at) {
            startDate = new Date(editingCouponData.generated_at);
          } else if (editingCouponData.created_at) {
            startDate = new Date(editingCouponData.created_at);
          } else {
            startDate = new Date(); // Fallback to today
          }
        } else {
          startDate = new Date(); // Today for new coupons
        }
        
        // Validate date
        if (isNaN(startDate.getTime())) {
          return `Generated Date + ${days} days`;
        }
        
        const expiredDate = new Date(startDate);
        expiredDate.setDate(expiredDate.getDate() + days - 1);
        expiredDate.setHours(23, 59, 59, 999);
        
        // Validate expired date
        if (isNaN(expiredDate.getTime())) {
          return `Generated Date + ${days} days`;
        }
        
        try {
          return format(expiredDate, 'MMM dd, yyyy');
        } catch (e) {
          console.error('Error formatting date:', e);
          return `Generated Date + ${days} days`;
        }
      } else {
        // For issued_date: Show "Issued Date + X days"
        return `Issued Date + ${days} days`;
      }
    } catch (error) {
      console.error('Error calculating validity display:', error);
      return 'Error calculating date';
    }
  };

  const getValidityDisplay = (coupon) => {
    try {
      // Debug logging in development
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 getValidityDisplay called with coupon:', {
          id: coupon.id || coupon.coupon_id,
          name: coupon.coupon_name || coupon.name,
          validity_start_type: coupon.validity_start_type,
          validity_days: coupon.validity_days,
          valid_until: coupon.valid_until,
          is_template: coupon.is_template,
          customer_phone: coupon.customer_phone,
        });
      }
      
      const validityStartType = coupon.validity_start_type || 'issued_date';
      const validityDays = coupon.validity_days;
      const isTemplate = coupon.is_template === true || (coupon.is_template === null && !coupon.customer_phone);
      
      if (!validityDays) {
        return { display: 'N/A', tooltip: 'No validity days set' };
      }

      if (validityStartType === 'generated_date') {
        // For generated_date: Show the actual expired date (already calculated from generation date)
        if (coupon.valid_until) {
          try {
            const expiredDate = format(new Date(coupon.valid_until), 'MMM dd, yyyy');
            const generatedDate = coupon.generated_at || coupon.created_at || coupon.start_date;
            const genDateStr = generatedDate ? format(new Date(generatedDate), 'MMM dd, yyyy') : 'generation date';
            return {
              display: expiredDate,
              tooltip: `Expires: ${expiredDate} (calculated from ${genDateStr} + ${validityDays} days)`,
              subtitle: `From Generated Date`
            };
          } catch (e) {
            return {
              display: `Generated + ${validityDays} days`,
              tooltip: `Validity calculated from coupon generation date + ${validityDays} days`,
              subtitle: `From Generated Date`
            };
          }
        } else {
          return {
            display: `Generated + ${validityDays} days`,
            tooltip: `Validity calculated from coupon generation date + ${validityDays} days`,
            subtitle: `From Generated Date`
          };
        }
      } else {
        // For issued_date: 
        // - If template: Show "Issued Date + X days" (valid_until is placeholder)
        // - If issued coupon: Show actual expired date (calculated from issue date)
        if (isTemplate) {
          return {
            display: `Issued Date + ${validityDays} days`,
            tooltip: `Validity will be calculated from coupon issue date (bill generation date) + ${validityDays} days when assigned to customer`,
            subtitle: `From Issued Date`
          };
        } else {
          // Issued coupon - show actual expired date
          if (coupon.valid_until) {
            try {
              const expiredDate = format(new Date(coupon.valid_until), 'MMM dd, yyyy');
              const issuedDate = coupon.generated_at || coupon.created_at;
              const issueDateStr = issuedDate ? format(new Date(issuedDate), 'MMM dd, yyyy') : 'issue date';
              return {
                display: expiredDate,
                tooltip: `Expires: ${expiredDate} (calculated from ${issueDateStr} + ${validityDays} days)`,
                subtitle: `From Issued Date`
              };
            } catch (e) {
              return {
                display: `Issued Date + ${validityDays} days`,
                tooltip: `Validity calculated from coupon issue date + ${validityDays} days`,
                subtitle: `From Issued Date`
              };
            }
          } else {
            return {
              display: `Issued Date + ${validityDays} days`,
              tooltip: `Validity calculated from coupon issue date + ${validityDays} days`,
              subtitle: `From Issued Date`
            };
          }
        }
      }
    } catch (error) {
      console.error('Error in getValidityDisplay:', error, coupon);
      return { 
        display: 'N/A', 
        tooltip: 'Error calculating validity',
        subtitle: null
      };
    }
  };

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    const errorStatus = error?.response?.status;
    const errorMessage = error?.response?.data?.message || error?.message || 'Failed to load coupons. Please try again.';
    
    // Handle 401 Unauthorized (token expired/invalid)
    if (errorStatus === 401) {
      // Check if error message indicates expired token
      const errorMsgLower = errorMessage.toLowerCase();
      const isExpired = errorMsgLower.includes('expired') || errorMsgLower.includes('token expired') || errorMsgLower.includes('session expired');
      
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          <Typography variant="h6" gutterBottom>
            {isExpired ? 'Session Expired' : 'Authentication Required'}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isExpired 
              ? 'Your session has expired. Please log in again to continue.'
              : 'You need to be logged in to access this page. Please log in and try again.'}
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => {
              try {
                // Clear tokens
                localStorage.removeItem('admin_token');
                localStorage.removeItem('admin_user');
                localStorage.removeItem('admin_devUser');
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                // Redirect to login
                window.location.href = '/aalogin';
              } catch (e) {
                console.error('Error clearing tokens:', e);
                window.location.href = '/aalogin';
              }
            }}
            sx={{ mt: 1 }}
          >
            Go to Login Page
          </Button>
        </Alert>
      );
    }
    
    // Handle other errors
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <Typography variant="h6" gutterBottom>Failed to load coupons</Typography>
        <Typography variant="body2">{errorMessage}</Typography>
        {errorStatus && (
          <Typography variant="caption" color="text.secondary">
            Status: {errorStatus}
          </Typography>
        )}
        {process.env.NODE_ENV === 'development' && errorStatus !== 401 && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" component="pre" sx={{ fontSize: '0.7rem', overflow: 'auto' }}>
              {JSON.stringify(error, null, 2)}
            </Typography>
          </Box>
        )}
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        mb: 3, 
        mt: embedded ? 0 : 2, 
        gap: 2 
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!embedded && (
            <Typography variant="h4" component="h1" fontWeight="bold">
              Coupons Management
            </Typography>
          )}
          <Typography variant={embedded ? 'subtitle1' : 'body2'} color="text.secondary" sx={{ mt: embedded ? 0 : 1 }} fontWeight={embedded ? 600 : 400}>
            {embedded
              ? 'Create and manage coupon templates (Share with Locsy on by default for DigiDrop).'
              : 'Create coupons (Share with Locsy on by default for DigiDrop). Pause, remove from Locsy, or delete anytime.'}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={handleAdd}
            fullWidth={false}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Create Coupon
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              fullWidth
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 auto', minWidth: 200 } }}
              placeholder="Search coupons..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Type Filter</InputLabel>
              <Select
                value={typeFilter}
                label="Type Filter"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
                <MenuItem value="partner">Partner</MenuItem>
                <MenuItem value="seasonal">Seasonal</MenuItem>
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>Share with Locsy</InputLabel>
              <Select
                value={shareFilter}
                label="Share with Locsy"
                onChange={(e) => {
                  setShareFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="shared">Shared with Locsy</MenuItem>
                <MenuItem value="private">Private (shop only)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Share with Locsy:</strong> Shared coupons can be randomly unlocked by citizens after they check your ads (DigiDrop). Same citizen won’t get the same coupon again within 30 days if another is available. On bills, use Retrieve &amp; Apply — you may edit or refuse; Locsy does not force honouring.
          </Alert>

          {/* Status Tabs - Similar to User Dashboard */}
          <Box sx={{ mb: 3 }}>
            <Tabs
              value={statusFilter}
              onChange={(e, newValue) => {
                setStatusFilter(newValue);
                setPage(1); // Reset to first page when switching tabs
              }}
              variant={isMobile ? 'scrollable' : 'standard'}
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minWidth: { xs: 80, sm: 120 },
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 600,
                  textTransform: 'none',
                },
              }}
            >
              <Tab 
                label={`Active (${allCouponsData?.coupons?.filter(isTemplateCouponActive).length || 0})`}
                value="active"
              />
              <Tab 
                label={`Used (${allCouponsData?.coupons?.filter(isTemplateCouponUsed).length || 0})`}
                value="used"
              />
              <Tab 
                label={`Expired (${allCouponsData?.coupons?.filter(isTemplateCouponExpired).length || 0})`}
                value="expired"
              />
              <Tab
                label="Allotted"
                value="allotted"
              />
              <Tab
                label={`Recycle Bin (${trashCountData || 0})`}
                value="trash"
              />
            </Tabs>
          </Box>

          {/* Selection Bar - Visible when items are selected */}
          {selectedCoupons.size > 0 && (
            <Box 
              sx={{ 
                mb: 2,
                p: 2.5,
                bgcolor: 'error.light',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: 3,
                border: '2px solid',
                borderColor: 'error.main',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                  {selectedCoupons.size} Coupon(s) Selected
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<DeleteIcon />}
                onClick={() => handleDeleteClick(null)}
                disabled={bulkDeleting}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  bgcolor: 'white',
                  color: 'error.main',
                  '&:hover': {
                    bgcolor: 'error.main',
                    color: 'white',
                  }
                }}
              >
                {bulkDeleting ? 'Deleting...' : `Delete ${selectedCoupons.size} Selected`}
              </Button>
            </Box>
          )}

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Tooltip title={selectAll ? "Deselect all matching coupons" : "Select all matching coupons (across all pages)"}>
                      <Checkbox
                        checked={selectAll}
                        indeterminate={selectedCoupons.size > 0 && selectedCoupons.size < (data?.pagination?.total || 0)}
                        onChange={handleSelectAll}
                        color="primary"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>Coupon Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Service / Offer</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Validity / Expired Date</TableCell>
                  <TableCell>Uses / Loksy allotments</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.coupons?.filter(coupon => coupon != null).map((coupon, index) => {
                  const couponId = coupon.id || coupon.coupon_id || `coupon-${index}`;
                  const isSelected = selectedCoupons.has(String(couponId));
                  return (
                    <TableRow key={couponId} hover selected={isSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectCoupon(couponId)}
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CouponIcon color="primary" />
                        <Box>
                          <Typography fontWeight="medium">{coupon.coupon_name || coupon.name}</Typography>
                          {(coupon.share_with_locsy === true || coupon.shareWithLocsy === true) ? (
                            <Chip
                              icon={<CampaignIcon sx={{ fontSize: '14px !important' }} />}
                              label="Shared with Locsy"
                              size="small"
                              color="secondary"
                              sx={{ mt: 0.5, height: 22, fontSize: '0.7rem' }}
                            />
                          ) : (
                            <Chip
                              icon={<LinkOffIcon sx={{ fontSize: '14px !important' }} />}
                              label="Not on Loksy"
                              size="small"
                              variant="outlined"
                              sx={{ mt: 0.5, height: 22, fontSize: '0.7rem' }}
                            />
                          )}
                          {(() => {
                            const professions = fromAllowedProfessionsPayload(coupon.allowed_professions);
                            const isAll = professions.length === 1 && professions[0] === ALL_PROFESSION;
                            return (
                              <Chip
                                label={isAll ? 'Professions: ALL' : `Professions: ${professions.join(', ')}`}
                                size="small"
                                color={isAll ? 'default' : 'warning'}
                                variant={isAll ? 'outlined' : 'filled'}
                                sx={{ mt: 0.5, mr: 0.5, height: 22, fontSize: '0.65rem', maxWidth: 280 }}
                              />
                            );
                          })()}
                          {coupon.service_name && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {coupon.service_name}
                            </Typography>
                          )}
                          {coupon.description && (
                            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ color: 'info.main', fontWeight: 500 }}>
                                Note:
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {coupon.description}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {coupon.coupon_code || coupon.code}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 300 }}>
                        {getOfferDisplay(coupon)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={coupon.coupon_type || 'internal'}
                        color={getTypeColor(coupon.coupon_type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const raw = coupon.created_at || coupon.generated_at;
                        if (!raw) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          );
                        }
                        try {
                          const d = new Date(raw);
                          return (
                            <Tooltip title={d.toLocaleString()} arrow>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {format(d, 'dd/MM/yyyy')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {format(d, 'HH:mm')}
                                </Typography>
                              </Box>
                            </Tooltip>
                          );
                        } catch {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          );
                        }
                      })()}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        try {
                          if (!coupon) {
                            return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                          }
                          
                          const validityInfo = getValidityDisplay(coupon);
                          
                          if (!validityInfo || !validityInfo.display) {
                            console.warn('⚠️ No validity info returned for coupon:', coupon);
                            return (
                              <Typography variant="body2" color="text.secondary">
                                {coupon.validity_days ? `${coupon.validity_days} days` : 'N/A'}
                              </Typography>
                            );
                          }
                          
                          return (
                            <Tooltip title={validityInfo.tooltip || validityInfo.display || 'N/A'} arrow>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {validityInfo.display}
                                </Typography>
                                {validityInfo.subtitle && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                                    {validityInfo.subtitle}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          );
                        } catch (err) {
                          console.error('❌ Error displaying validity:', err, coupon);
                          return (
                            <Typography variant="body2" color="error">
                              Error
                            </Typography>
                          );
                        }
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        Used {coupon.current_uses || 0}
                        {coupon.max_uses ? ` / ${coupon.max_uses}` : ''}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Loksy ad allotments:{' '}
                        {(() => {
                          const fromApi = Number(coupon.locsy_allotment_count);
                          if (!Number.isNaN(fromApi) && fromApi > 0) return fromApi;
                          const qty = Number(coupon.digidrop_pool_qty);
                          const rem = Number(coupon.digidrop_pool_remaining);
                          if (
                            !Number.isNaN(qty) &&
                            !Number.isNaN(rem) &&
                            qty >= rem
                          ) {
                            return qty - rem;
                          }
                          return Number.isNaN(fromApi) ? 0 : fromApi;
                        })()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={coupon.status}
                        color={getStatusColor(coupon.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {(coupon.share_with_locsy === true || coupon.shareWithLocsy === true) ? (
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleToggleShare(coupon, false)}
                            disabled={toggleShareMutation.isLoading}
                            title="Remove from Locsy"
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleToggleShare(coupon, true)}
                            disabled={toggleShareMutation.isLoading || coupon.status === 'expired'}
                            title="Share with Locsy"
                          >
                            <CampaignIcon fontSize="small" />
                          </IconButton>
                        )}
                        {statusFilter === 'trash' ? (
                          <>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => {
                                const id = coupon.id || coupon.coupon_id;
                                restoreMutation.mutate(id);
                              }}
                              disabled={restoreMutation.isLoading}
                              title="Restore"
                            >
                              <RestoreIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setDeleteDialog({
                                  ...coupon,
                                  permanent: true,
                                })
                              }
                              title="Delete forever"
                            >
                              <DeleteForeverIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                        {coupon.status === 'active' ? (
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleToggleStatus(coupon, 'pause')}
                            disabled={toggleStatusMutation.isLoading || coupon.status === 'expired'}
                            title="Pause coupon"
                          >
                            <PauseIcon />
                          </IconButton>
                        ) : coupon.status === 'inactive' ? (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleToggleStatus(coupon, 'resume')}
                            disabled={toggleStatusMutation.isLoading || coupon.status === 'expired'}
                            title="Resume coupon"
                          >
                            <PlayArrowIcon />
                          </IconButton>
                        ) : null}
                        <IconButton size="small" onClick={() => handleEdit(coupon)} title="Edit coupon">
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(coupon)}
                          title="Move to recycle bin"
                        >
                          <DeleteIcon />
                        </IconButton>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {data?.coupons?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No coupons found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {data?.pagination && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={data.pagination.totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setServiceInputValue('');
        }}
        maxWidth="md"
        fullWidth
        scroll="body"
      >
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>
            {editingCoupon ? 'Edit Coupon' : 'Create Service-Based Coupon'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Service-Based Coupon Toggle - At the top */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      id="is_service_based"
                      name="is_service_based"
                      checked={formik.values.is_service_based}
                      onChange={(e) => {
                        formik.setFieldValue('is_service_based', e.target.checked);
                        if (!e.target.checked) {
                          formik.setFieldValue('service_id', '');
                          formik.setFieldValue('service_name', '');
                          setServiceInputValue('');
                        }
                      }}
                    />
                  }
                  label="Service-Based Coupon (with unique offer model)"
                />
              </Grid>

              {/* Service & Pricing Details Section - First */}
              {formik.values.is_service_based && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>
                      Product / Service & Pricing
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      freeSolo
                      options={services}
                      getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return `${option.name} - ${formatRs(option.price || 0)}${option.is_active === false ? ' (Inactive)' : ''}`;
                      }}
                      value={formik.values.service_id 
                        ? services.find(s => s.id === formik.values.service_id) || null
                        : null
                      }
                      inputValue={serviceInputValue}
                      onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'reset') return;
                        handleServiceInputChange(event, newInputValue, reason);
                      }}
                      onChange={(event, newValue, reason) => {
                        handleServiceInputChange(event, newValue, reason);
                      }}
                      onBlur={handleServiceInputBlur}
                      loading={servicesLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Type or select product / service"
                          required
                          helperText="Pick an existing item — list price loads; set offer price → worth auto-fills."
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name} - {formatRs(option.price || 0)}
                          {option.is_active === false && ' (Inactive)'}
                        </li>
                      )}
                      filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;
                        const searchLower = inputValue.toLowerCase();
                        return options.filter(service =>
                          service.name.toLowerCase().includes(searchLower) ||
                          (service.main_category && service.main_category.toLowerCase().includes(searchLower)) ||
                          (service.sub_category && service.sub_category.toLowerCase().includes(searchLower))
                        );
                      }}
                      noOptionsText={
                        serviceInputValue.trim() 
                          ? `No service found. Press Enter or click outside to add "${serviceInputValue}"`
                          : 'Start typing to search services...'
                      }
                    />
                    {services.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                        {services.length} service{services.length !== 1 ? 's' : ''} available. Type to search or add new.
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="actual_price"
                      name="actual_price"
                      label="List / Actual Price (Rs.)"
                      type="number"
                      value={formik.values.actual_price}
                      onChange={(e) => {
                        formik.setFieldValue('actual_price', e.target.value);
                        syncCouponWorthFromPrices(
                          e.target.value,
                          formik.values.offer_price
                        );
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.actual_price && Boolean(formik.errors.actual_price)}
                      helperText={
                        formik.touched.actual_price && formik.errors.actual_price
                          ? formik.errors.actual_price
                          : 'Loaded from product/service when you pick one'
                      }
                      required
                      inputProps={{ step: '0.01', min: '0.01' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="offer_price"
                      name="offer_price"
                      label="Offer Price (Rs.)"
                      type="number"
                      value={formik.values.offer_price}
                      onChange={(e) => {
                        formik.setFieldValue('offer_price', e.target.value);
                        syncCouponWorthFromPrices(
                          formik.values.actual_price,
                          e.target.value
                        );
                        // If day-wise increasing and day 1 exists, update it
                        if (formik.values.offer_type === 'increasing' && dayWisePricing['1']) {
                          setDayWisePricing({ ...dayWisePricing, '1': e.target.value });
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.offer_price && Boolean(formik.errors.offer_price)}
                      helperText={
                        formik.touched.offer_price && formik.errors.offer_price
                          ? formik.errors.offer_price
                          : (() => {
                              const a = parseFloat(formik.values.actual_price);
                              const o = parseFloat(formik.values.offer_price);
                              if (!Number.isNaN(a) && !Number.isNaN(o) && a > o) {
                                return `Worth auto = list − offer = Rs. ${Math.round((a - o) * 100) / 100}`;
                              }
                              return formik.values.offer_type === 'fixed'
                                ? `Same price (${formatRs(formik.values.offer_price || 0)}) for all ${formik.values.validity_days || 'validity'} days`
                                : 'Default price for Day 1 in day-wise increasing offers';
                            })()
                      }
                      required
                      inputProps={{ step: '0.01', min: '0.01' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Offer Type</InputLabel>
                      <Select
                        id="offer_type"
                        name="offer_type"
                        value={formik.values.offer_type}
                        onChange={(e) => {
                          formik.setFieldValue('offer_type', e.target.value);
                          // If switching to increasing and offer_price exists, set day 1
                          if (e.target.value === 'increasing' && formik.values.offer_price) {
                            if (!dayWisePricing['1']) {
                              setDayWisePricing({ ...dayWisePricing, '1': formik.values.offer_price });
                            }
                          }
                        }}
                        label="Offer Type"
                      >
                        {offerTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl component="fieldset" required>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Validity Start Date *
                      </Typography>
                      <RadioGroup
                        row
                        name="validity_start_type"
                        value={formik.values.validity_start_type || 'issued_date'}
                        onChange={formik.handleChange}
                        sx={{ gap: 3 }}
                      >
                        <FormControlLabel
                          value="issued_date"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                From Coupon Issued Date
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Validity counts from when coupon is issued to user (bill generation date). 
                                Example: Admin generates on 8th Dec, but coupon issued on 11th Dec. 
                                If validity is 3 days, it will be valid from 11th, 12th, 13th.
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="generated_date"
                          control={<Radio />}
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                From Coupon Generated Date
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                Validity counts from when admin generated the coupon. 
                                Example: Generated on 8th Dec, valid for 3 days means 8th, 9th, 10th. 
                                If issued on 11th Dec, it would already be expired.
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="validity_days"
                      name="validity_days"
                      label="Validity (Days)"
                      type="number"
                      value={formik.values.validity_days}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.validity_days && Boolean(formik.errors.validity_days)}
                      helperText={
                        formik.touched.validity_days && formik.errors.validity_days
                          ? formik.errors.validity_days
                          : formik.values.validity_start_type === 'issued_date'
                          ? 'Number of days coupon is valid from the day it is issued to the user'
                          : 'Number of days coupon is valid from the day it is generated'
                      }
                      required
                      inputProps={{ min: '1', max: '365' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Expired Date / Validity Display"
                      value={calculateValidityDisplay(
                        formik.values.validity_days,
                        formik.values.validity_start_type,
                        editingCoupon
                      )}
                      InputProps={{
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiInputBase-input': {
                          backgroundColor: 'action.selected',
                          fontWeight: 500,
                          color: 'primary.main',
                        }
                      }}
                      helperText={
                        formik.values.validity_start_type === 'generated_date'
                          ? editingCoupon 
                            ? 'Expired date calculated from coupon generation date'
                            : 'Expired date calculated from today (generation date)'
                          : 'Validity will be calculated when coupon is issued to customer'
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mt: 1 }}>
                      {formik.values.validity_start_type === 'issued_date' ? (
                        <>
                          Coupon validity starts from the day it is issued to the user (bill generation date). 
                          The coupon will be valid for {formik.values.validity_days || 'X'} days from the issue date.
                        </>
                      ) : (
                        <>
                          Coupon validity starts from the day it is generated (today). 
                          The coupon will be valid for {formik.values.validity_days || 'X'} days from generation date.
                        </>
                      )}
                    </Alert>
                  </Grid>

                  {/* Day-wise Increasing Offer */}
                  {formik.values.offer_type === 'increasing' && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Day-wise Dynamic Pricing (Set price for specific days)
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<AddIconSmall />}
                              onClick={handleAddDayWisePrice}
                              variant="outlined"
                            >
                              Add Day
                            </Button>
                          </Box>
                          {formik.values.validity_days && (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                              Valid for {formik.values.validity_days} days. Set prices for specific days (Day 1, Day 2, etc. from coupon generation date).
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {Object.keys(dayWisePricing)
                              .sort((a, b) => Number(a) - Number(b))
                              .map((day) => {
                                const dayNum = Number(day);
                                const isValidDay = dayNum >= 1 && dayNum <= (formik.values.validity_days || 365);
                                return (
                                  <Box key={day} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <TextField
                                      label={`Day ${day}`}
                                      type="number"
                                      value={day}
                                      onChange={(e) => {
                                        const newDay = e.target.value;
                                        if (newDay && !isNaN(newDay) && Number(newDay) > 0) {
                                          const price = dayWisePricing[day];
                                          const newPricing = { ...dayWisePricing };
                                          delete newPricing[day];
                                          newPricing[newDay] = price;
                                          setDayWisePricing(newPricing);
                                        }
                                      }}
                                      sx={{ width: 150 }}
                                      inputProps={{ min: '1', max: formik.values.validity_days || 365 }}
                                      required
                                      error={!isValidDay}
                                      helperText={!isValidDay ? `Must be 1-${formik.values.validity_days}` : ''}
                                    />
                                    <TextField
                                      label={`Price for Day ${day} (Rs.)`}
                                      type="number"
                                      value={dayWisePricing[day] || ''}
                                      onChange={(e) => handleDayWisePriceChange(day, e.target.value)}
                                      sx={{ flex: 1 }}
                                      inputProps={{ step: '0.01', min: '0.01' }}
                                      required
                                    />
                                    <IconButton
                                      color="error"
                                      onClick={() => handleRemoveDayWisePrice(day)}
                                      size="small"
                                    >
                                      <DeleteIconSmall />
                                    </IconButton>
                                  </Box>
                                );
                              })}
                            {Object.keys(dayWisePricing).length === 0 && (
                              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                Click "Add Day" to set dynamic pricing for specific days. 
                                Example: Day 1: Rs.50, Day 2: Rs.60, Day 8: Rs.80
                                {formik.values.validity_days && (
                                  <> (Valid for {formik.values.validity_days} days)</>
                                )}
                              </Typography>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </>
              )}

              {/* Place coupon (% / flat / Buy X Get Y) — turn OFF service-based */}
              {!formik.values.is_service_based && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
                      Place coupon type
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Buy X Get Y: bill lines = X at full price, Y at ₹0 (no extra deduction).
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      fullWidth
                      SelectProps={{ native: true }}
                      id="discount_type"
                      name="discount_type"
                      label="Coupon Type"
                      value={formik.values.discount_type}
                      onChange={(e) => {
                        const v = e.target.value;
                        formik.setFieldValue('discount_type', v);
                        if (v === 'buy_x_get_y') {
                          const x = String(formik.values.buy_product_name || '').trim();
                          const y = String(formik.values.get_product_name || '').trim();
                          if (x && y) {
                            formik.setFieldValue('coupon_name', `Buy ${x} Get ${y}`);
                          }
                        }
                      }}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="flat">Flat Discount (Rs.)</option>
                      <option value="buy_x_get_y">Buy X Get Y</option>
                    </TextField>
                  </Grid>
                  {formik.values.discount_type !== 'buy_x_get_y' && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        id="discount_value"
                        name="discount_value"
                        label={
                          formik.values.discount_type === 'percentage'
                            ? 'Discount %'
                            : 'Discount Amount (Rs.)'
                        }
                        type="number"
                        value={formik.values.discount_value}
                        onChange={(e) => {
                          formik.setFieldValue('discount_value', e.target.value);
                          formik.setFieldValue('coupon_worth', e.target.value);
                        }}
                        required
                      />
                    </Grid>
                  )}
                  {formik.values.discount_type === 'buy_x_get_y' && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          freeSolo
                          options={services}
                          getOptionLabel={(option) =>
                            typeof option === 'string'
                              ? option
                              : `${option.name} - ${formatRs(option.price || 0)}`
                          }
                          onChange={(_, newValue) => {
                            if (newValue && typeof newValue === 'object') {
                              formik.setFieldValue('buy_product_id', newValue.id || '');
                              formik.setFieldValue('buy_product_name', newValue.name || '');
                              formik.setFieldValue('buy_unit_price', String(newValue.price || ''));
                              const y = String(formik.values.get_product_name || '').trim();
                              if (y) {
                                formik.setFieldValue(
                                  'coupon_name',
                                  `Buy ${newValue.name} Get ${y}`
                                );
                              }
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Buy product (X) *" required />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          id="buy_unit_price"
                          name="buy_unit_price"
                          label="X list price (Rs.)"
                          type="number"
                          value={formik.values.buy_unit_price}
                          onChange={formik.handleChange}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          freeSolo
                          options={services}
                          getOptionLabel={(option) =>
                            typeof option === 'string'
                              ? option
                              : `${option.name} - ${formatRs(option.price || 0)}`
                          }
                          onChange={(_, newValue) => {
                            if (newValue && typeof newValue === 'object') {
                              formik.setFieldValue('get_product_id', newValue.id || '');
                              formik.setFieldValue('get_product_name', newValue.name || '');
                              formik.setFieldValue('get_unit_price', String(newValue.price || ''));
                              formik.setFieldValue('discount_value', String(newValue.price || ''));
                              formik.setFieldValue('coupon_worth', String(newValue.price || ''));
                              const x = String(formik.values.buy_product_name || '').trim();
                              if (x) {
                                formik.setFieldValue(
                                  'coupon_name',
                                  `Buy ${x} Get ${newValue.name}`
                                );
                              }
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Get free product (Y) *" required />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          id="get_unit_price"
                          name="get_unit_price"
                          label="Y list price (Rs.) — free worth"
                          type="number"
                          value={formik.values.get_unit_price}
                          onChange={(e) => {
                            formik.setFieldValue('get_unit_price', e.target.value);
                            formik.setFieldValue('discount_value', e.target.value);
                            formik.setFieldValue('coupon_worth', e.target.value);
                          }}
                          required
                        />
                      </Grid>
                    </>
                  )}
                </>
              )}

              {/* Basic Information Section - After Service Details */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Basic Information
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="coupon_name"
                  name="coupon_name"
                  label="Coupon Name"
                  value={formik.values.coupon_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.coupon_name && Boolean(formik.errors.coupon_name)}
                  helperText={formik.touched.coupon_name && formik.errors.coupon_name}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="coupon_code"
                  name="coupon_code"
                  label="Coupon Code"
                  value={formik.values.coupon_code}
                  onChange={(e) => {
                    setCouponCodeManual(true);
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.coupon_code && Boolean(formik.errors.coupon_code)}
                  helperText={
                    formik.touched.coupon_code && formik.errors.coupon_code
                      ? formik.errors.coupon_code
                      : 'Auto-filled from coupon name & offer price. Tap ↻ to regenerate (your account only).'
                  }
                  placeholder="e.g. PVC75"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Generate code from name & price">
                          <span>
                            <IconButton
                              edge="end"
                              onClick={() => applyAutoCouponCode({ force: true })}
                              disabled={generatingCouponCode}
                              aria-label="Auto-generate coupon code"
                            >
                              {generatingCouponCode ? (
                                <CircularProgress size={22} />
                              ) : (
                                <AutorenewIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Coupon Type</InputLabel>
                  <Select
                    id="coupon_type"
                    name="coupon_type"
                    value={formik.values.coupon_type}
                    onChange={formik.handleChange}
                    label="Coupon Type"
                  >
                    {couponTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="description"
                  name="description"
                  label="Description (Optional)"
                  multiline
                  rows={3}
                  value={formik.values.description}
                  onChange={formik.handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      id="share_with_locsy"
                      name="share_with_locsy"
                      checked={formik.values.share_with_locsy !== false}
                      onChange={(e) =>
                        formik.setFieldValue('share_with_locsy', e.target.checked)
                      }
                      color="secondary"
                    />
                  }
                  label={
                    <Box>
                      <Typography fontWeight={600}>Share with Locsy</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Citizens who check your pamphlets, popups &amp; updates can randomly unlock this coupon (DigiDrop). Recommended — turn off for shop-only offers.
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
              {formik.values.share_with_locsy !== false && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="coupon_worth"
                      name="coupon_worth"
                      label="Coupon worth (Rs.)"
                      type="number"
                      value={formik.values.coupon_worth}
                      onChange={formik.handleChange}
                      helperText="Higher worth → popup/pamphlet tiers. Auto-fills as list − offer when you set prices."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      id="digidrop_pool_qty"
                      name="digidrop_pool_qty"
                      label="DigiDrop pool quantity"
                      type="number"
                      value={formik.values.digidrop_pool_qty}
                      onChange={formik.handleChange}
                      helperText="How many citizens can unlock this (e.g. 100)"
                    />
                  </Grid>
                </>
              )}

              {/* Profession Selection — shared list with Smart Bill */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Profession Restrictions (Optional)
                </Typography>
                <ProfessionRestrictionSelect
                  selected={selectedProfessions}
                  onChange={setSelectedProfessions}
                  otherText={otherProfessionText}
                  onOtherTextChange={setOtherProfessionText}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? (
                <CircularProgress size={24} />
              ) : editingCoupon ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete / Recycle Dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)}>
        <DialogTitle>
          {deleteDialog?.bulk
            ? `Delete ${deleteDialog.count} Coupons`
            : deleteDialog?.permanent
              ? 'Permanently Delete Coupon'
              : 'Move to Recycle Bin'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialog?.bulk ? (
              <>Are you sure you want to delete {deleteDialog.count} selected coupon(s)? Bulk delete is permanent.</>
            ) : deleteDialog?.permanent ? (
              <>Permanently delete "{deleteDialog?.coupon_name || deleteDialog?.name}"? This cannot be undone.</>
            ) : (
              <>Move "{deleteDialog?.coupon_name || deleteDialog?.name}" to Recycle Bin? You can restore it later.</>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleteMutation.isLoading || bulkDeleting}
          >
            {(deleteMutation.isLoading || bulkDeleting) ? <CircularProgress size={24} /> : (deleteDialog?.permanent ? 'Delete forever' : 'Move to Bin')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add New Service Dialog */}
      <Dialog
        open={showNewServiceDialog}
        onClose={handleCancelNewService}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Service</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Service Name"
                value={newServiceData.name}
                onChange={(e) => setNewServiceData({ ...newServiceData, name: e.target.value })}
                required
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Main Category"
                value={newServiceData.main_category}
                onChange={(e) => setNewServiceData({ ...newServiceData, main_category: e.target.value })}
                placeholder="e.g., Online, PVC, Photo"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sub Category"
                value={newServiceData.sub_category}
                onChange={(e) => setNewServiceData({ ...newServiceData, sub_category: e.target.value })}
                placeholder="e.g., Application, Card"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Service Charge (Rs.)"
                type="number"
                value={newServiceData.price}
                onChange={(e) => setNewServiceData({ ...newServiceData, price: e.target.value })}
                required
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Challana (Rs.)"
                type="number"
                value={newServiceData.challana}
                onChange={(e) => setNewServiceData({ ...newServiceData, challana: e.target.value })}
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelNewService} disabled={savingNewService}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveNewService}
            variant="contained"
            color="primary"
            disabled={savingNewService || !newServiceData.name?.trim()}
            startIcon={savingNewService ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {savingNewService ? 'Saving...' : 'Save & Use'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CouponsManagement;

